import { useTranslate } from "@opensea/next-translate"
import type { ItemProps } from "@opensea/ui-kit"
import {
  Item,
  Skeleton,
  Spinner,
  UnstyledButton,
  classNames,
  twDataIdSelector,
} from "@opensea/ui-kit"
import { List } from "@opensea/ui-kit/client"
import {
  CheckCircleFilled,
  CancelFilled,
  ChevronRight,
  MoreHoriz,
  OpenInNew,
  WarningFilled,
} from "@opensea/ui-kit/icons"
import React, { useState } from "react"
import { captureException } from "@sentry/nextjs"
import { isBefore, parseISO, subHours } from "date-fns"
import { TokenAmount } from "@/components/common/TokenAmount"
import { Dropdown } from "@/design-system/Dropdown"
import { Overflow } from "@/design-system/Overflow"
import type { NumberInput } from "@/lib/helpers/numberUtils"
import { displayFiat } from "@/lib/helpers/numberUtils"
import { useWalletActions } from "@/providers/Wallet/useWalletActions"
import { useTrackingFn } from "@/lib/analytics/useTrackingFn"
import { Tooltip } from "@/design-system/Tooltip"
import type { TxState } from "../types"

type TxItemBaseProps = ItemProps

function TxItemBase({ className, ...restProps }: TxItemBaseProps) {
  return (
    <List.Item
      className={classNames("p-3 hover:bg-component-gray-1", className)}
      {...restProps}
    />
  )
}

type TxItemAvatarProps = {
  children: React.ReactNode
  state: TxState
}

function TxItemAvatar({ children, state }: TxItemAvatarProps) {
  const isComplete =
    state === "SUCCESSFUL" || state === "CANCELLED" || state === "REJECTED"
  const renderState = () => {
    if (isComplete) {
      if (state === "SUCCESSFUL") {
        return (
          <CheckCircleFilled
            className="absolute -bottom-0.5 -right-1 rounded-full bg-elevation-2"
            fill="green-2"
          />
        )
      }
      return (
        <CancelFilled
          className="absolute -bottom-0.5 -right-1 rounded-full bg-elevation-2"
          fill="accent-red"
        />
      )
    }
    return null
  }
  return (
    <List.Item.Avatar className="relative overflow-visible" size={60}>
      {children}
      {renderState()}
    </List.Item.Avatar>
  )
}

type TxItemContentProps = {
  children: React.ReactNode
}

function TxItemContent({ children }: TxItemContentProps) {
  return (
    <List.Item.Content className="gap-0.5 text-start">
      {children}
    </List.Item.Content>
  )
}

type TxItemTitleProps = {
  children: React.ReactNode
}

function TxItemTitle({ children }: TxItemTitleProps) {
  return (
    <List.Item.Title className="w-full text-md">
      <Overflow>{children}</Overflow>
    </List.Item.Title>
  )
}

type TxItemDescriptionProps = {
  children: React.ReactNode
}

function TxItemDescription({ children }: TxItemDescriptionProps) {
  return (
    <List.Item.Description className="w-full text-sm">
      <Overflow>{children}</Overflow>
    </List.Item.Description>
  )
}

const TxItemValueDataId = "TxItemValue" as const

type TxItemValueProps = {
  symbol: string
  unit: NumberInput
  usd: NumberInput
  direction?: "sent" | "received"
}

const TxItemValue = Object.assign(
  ({ symbol, unit, usd, direction = "sent" }: TxItemValueProps) => {
    return (
      <List.Item.Side data-id={TxItemValueDataId}>
        <List.Item.Title className="text-md">
          {direction === "sent" ? "-" : "+"}
          <TokenAmount amount={unit} symbol={symbol} />
        </List.Item.Title>
        <List.Item.Description className="text-sm">
          {direction === "sent" ? "-" : "+"}
          {displayFiat(usd)}
        </List.Item.Description>
      </List.Item.Side>
    )
  },
  {
    dataId: TxItemValueDataId,
  },
)

function TxItemPending({
  nonce,
  href,
  createdDate,
}: {
  nonce?: number
  href: string | undefined
  createdDate?: string
}) {
  const t = useTranslate("transactions")

  // TODO use "cancelling" state from BE to determine this value
  const [isCanceling, setIsCanceling] = useState(false)
  const { cancel } = useWalletActions()
  const trackCancel = useTrackingFn<{ nonce: number; href?: string }>(
    "cancel transaction",
  )

  const renderIcon = () => {
    if (createdDate) {
      // if tx is pending for too long, prompt user to cancel
      const timestamp = parseISO(createdDate)
      const now = new Date()
      const oneHourAgo = subHours(now, 1)
      if (isBefore(timestamp, oneHourAgo)) {
        return (
          <Tooltip
            content={t(
              "expired",
              "This transaction is taking longer than expected to confirm. Click here to clear this transaction",
            )}
            variant="card"
          >
            <div>
              <WarningFilled
                className="cursor-pointer"
                fill="yellow-1"
                onClick={async () => {
                  if (nonce) {
                    try {
                      setIsCanceling(true)
                      trackCancel({ nonce, href })
                      await cancel(nonce)
                    } catch (e) {
                      captureException(e, {
                        extra: { nonce, href, action: "cancel" },
                      })
                    } finally {
                      setIsCanceling(false)
                    }
                  }
                }}
              />
            </div>
          </Tooltip>
        )
      }
    }
    return <Spinner />
  }

  return (
    <List.Item.Side className="flex-row items-center gap-2">
      {renderIcon()}
      <Dropdown
        // eslint-disable-next-line react/no-unstable-nested-components
        content={({ List, Item }) => (
          <List>
            <Item href={href} target="_blank">
              <Item.Avatar icon={OpenInNew} />
              <Item.Content>
                <Item.Title>{t("details", "Details")}</Item.Title>
              </Item.Content>
            </Item>
            {nonce !== undefined && (
              <Item
                disabled={isCanceling}
                onClick={async () => {
                  try {
                    setIsCanceling(true)
                    trackCancel({ nonce, href })
                    await cancel(nonce)
                  } catch (e) {
                    captureException(e, {
                      extra: { nonce, href, action: "cancel" },
                    })
                  } finally {
                    setIsCanceling(false)
                  }
                }}
              >
                {isCanceling ? (
                  <Spinner className="mr-4" color="gray-2" size="small" />
                ) : (
                  <Item.Avatar icon={CancelFilled} />
                )}
                <Item.Content>
                  <Item.Title color={isCanceling ? "secondary" : "current"}>
                    {t("cancel", "Cancel")}
                  </Item.Title>
                </Item.Content>
                <Item.Side />
              </Item>
            )}
          </List>
        )}
      >
        <UnstyledButton className="flex items-center p-2.5">
          <MoreHoriz />
        </UnstyledButton>
      </Dropdown>
    </List.Item.Side>
  )
}

function TxItemRequested() {
  return (
    <List.Item.Side>
      <ChevronRight />
    </List.Item.Side>
  )
}

function TxItemSkeleton() {
  return (
    <List.Item className="p-3">
      <List.Item.Avatar className="rounded-default" size={60}>
        <Skeleton.Block />
      </List.Item.Avatar>
      <List.Item.Content className="gap-2">
        <Skeleton.Line />
        <Skeleton.Line className="h-3.5" />
      </List.Item.Content>
      <List.Item.Side className="w-18 gap-2">
        <Skeleton.Line />
        <Skeleton.Line className="h-3.5" />
      </List.Item.Side>
    </List.Item>
  )
}

type TxItemListProps = {
  children: React.ReactNode
}

function TxItemList({ children }: TxItemListProps) {
  return (
    <List
      className={classNames(
        twDataIdSelector(Item, "[&_[data-id=Item]]:rounded-lg"),
        "bg-transparent px-3",
      )}
      showBorder={false}
    >
      {children}
    </List>
  )
}

export const TxItem = Object.assign(TxItemBase, {
  Avatar: TxItemAvatar,
  Content: TxItemContent,
  Title: TxItemTitle,
  Description: TxItemDescription,
  Value: TxItemValue,
  Pending: TxItemPending,
  Requested: TxItemRequested,
  Skeleton: TxItemSkeleton,
  List: TxItemList,
})
